/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unstable-nested-components, react/prop-types */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import INDataTable from "components/INDataTable";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API_LIST = `${protectedResources.apiTodoList.endpoint}/anticipateSetting/list`;
const API_CREATE = `${protectedResources.apiTodoList.endpoint}/anticipateSetting/create`;
const API_UPDATE = (id) =>
  `${protectedResources.apiTodoList.endpoint}/anticipateSetting/update/${id}`;
const API_DELETE = (id) =>
  `${protectedResources.apiTodoList.endpoint}/anticipateSetting/delete/${id}`;
const API_TAXONOMY_ALL = `${protectedResources.apiTodoList.endpoint}/taxonomy/listall`;
const API_COACH = `${protectedResources.apiTodoList.endpoint}/anticipateSetting/coach`;

const MIN_KEYWORD_WORDS = 6;

// Data sources are fixed — not user-configurable.
const DEFAULT_NEWS_PROVIDERS = ["gdelt", "bing", "tavily"];

const NEWS_PROVIDER_OPTIONS = [
  { id: "gdelt", label: "GDELT" },
  { id: "bing", label: "Bing News" },
  { id: "tavily", label: "Tavily" },
];

const INGEST_TIER_LABEL = { daily: "Daily batch", instant: "Instant / frequent" };

const STARTER_TEMPLATES = [
  {
    label: "Fuel cost",
    topic: "jet fuel",
    sector: "aviation airline",
    aspect: "price spike supply shortage disruption",
  },
  {
    label: "ATC / airspace",
    topic: "air traffic control",
    sector: "aviation airspace",
    aspect: "staffing shortage disruption flight delay",
  },
  {
    label: "Maintenance",
    topic: "aircraft maintenance",
    sector: "aviation airline",
    aspect: "regulatory compliance safety incident",
  },
  {
    label: "Crew shortage",
    topic: "pilot shortage OR crew shortage",
    sector: "aviation airline",
    aspect: "operational impact flight cancellation",
  },
];

function assembleQuery(topic, sector, aspect) {
  const parts = [];
  const t = (topic || "").trim();
  if (t) {
    const hasSpecial = /\b(OR|AND|NOT)\b/.test(t) || t.includes('"');
    parts.push(!hasSpecial && t.includes(" ") ? `"${t}"` : t);
  }
  const s = (sector || "").trim();
  if (s) parts.push(s);
  const a = (aspect || "").trim();
  if (a) parts.push(a);
  return parts.join(" ");
}

const INDUSTRY_TERMS = [
  "airline",
  "aviation",
  "aircraft",
  "airport",
  "flight",
  "crew",
  "pilot",
  "airspace",
  "qantas",
  "jet",
  "airway",
  "carrier",
  "atc",
  "runway",
  "terminal",
  "boeing",
  "airbus",
  "aeronautical",
  "helicopter",
];
const RISK_TERMS = [
  "risk",
  "disruption",
  "shortage",
  "crisis",
  "impact",
  "delay",
  "failure",
  "surge",
  "spike",
  "cost",
  "compliance",
  "regulation",
  "sanction",
  "incident",
  "accident",
  "outage",
  "breach",
  "penalty",
  "fine",
  "strike",
  "dispute",
  "warning",
  "recall",
  "grounding",
  "ban",
  "restriction",
  "investigation",
];

function scoreQuery(query) {
  if (!query || !query.trim()) return null;
  const q = query.toLowerCase();
  const words = q.trim().split(/\s+/).filter(Boolean);
  const hasQuotes = /"[^"]+"/.test(q);
  const hasBoolean = /\b(or|and)\b/.test(q);
  const hasIndustry = INDUSTRY_TERMS.some((t) => q.includes(t));
  const hasRisk = RISK_TERMS.some((t) => q.includes(t));
  let pts = 0;
  if (hasQuotes) pts += 2;
  if (hasIndustry) pts += 1;
  if (hasRisk) pts += 1;
  if (hasBoolean) pts += 1;
  if (words.length < 4 || pts < 2) {
    return {
      level: "red",
      label: "Too broad — add industry + risk context",
      pct: Math.max(10, pts * 8),
    };
  }
  if (pts >= 4) {
    return { level: "green", label: "Good query", pct: Math.min(100, 75 + pts * 5) };
  }
  return { level: "amber", label: "Decent — could be sharper", pct: 38 + pts * 10 };
}

function QualityMeter({ query }) {
  const score = scoreQuery(query);
  if (!score) return null;
  const color =
    score.level === "green" ? "#2e7d32" : score.level === "amber" ? "#ed6c02" : "#d32f2f";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={score.pct}
        sx={{
          flex: 1,
          height: 5,
          borderRadius: 4,
          bgcolor: "rgba(0,0,0,0.07)",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" sx={{ color, fontWeight: 600, whiteSpace: "nowrap" }}>
        {score.label}
      </Typography>
    </Box>
  );
}

function CoachResultPanel({ coachResult, onApply }) {
  const [detailOpen, setDetailOpen] = useState(false);
  if (!coachResult) return null;
  const issues = Array.isArray(coachResult.issues) ? coachResult.issues : [];

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, bgcolor: "#fafbff", borderColor: "rgba(82,0,155,0.2)" }}
    >
      {/* Issue chips */}
      {issues.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
          {issues.map((issue) => (
            <Chip
              key={issue}
              label={issue}
              size="small"
              sx={{
                bgcolor: "#fff3e0",
                color: "#b64200",
                border: "1px solid #ffb74d",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          ))}
        </Box>
      )}

      {/* Suggested query */}
      {coachResult.suggested_query ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            p: 1,
            bgcolor: "#eef2ff",
            border: "1px solid rgba(82,0,155,0.18)",
            borderRadius: 1,
            mb: coachResult.provider_note ? 1 : 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", flex: 1, wordBreak: "break-all", color: "#1a237e" }}
          >
            {coachResult.suggested_query}
          </Typography>
          <MDButton
            size="small"
            variant="contained"
            onClick={onApply}
            sx={{
              bgcolor: "#52009b",
              color: "#fff",
              whiteSpace: "nowrap",
              flexShrink: 0,
              fontSize: 11,
              px: 1.5,
              py: 0.5,
              minWidth: 0,
              "&:hover": { bgcolor: "#3a0070" },
            }}
          >
            Use this
          </MDButton>
        </Box>
      ) : null}

      {/* Provider note */}
      {coachResult.provider_note ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: coachResult.coaching ? 0.75 : 0 }}
        >
          {coachResult.provider_note}
        </Typography>
      ) : null}

      {/* Coaching detail — collapsible */}
      {coachResult.coaching ? (
        <>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setDetailOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setDetailOpen((v) => !v);
            }}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            <Typography variant="caption" sx={{ color: "#52009b", fontWeight: 600 }}>
              {detailOpen ? "▲ Hide detail" : "▼ More detail"}
            </Typography>
          </Box>
          <Collapse in={detailOpen}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                mt: 0.75,
                color: "text.secondary",
              }}
            >
              {coachResult.coaching}
            </Typography>
          </Collapse>
        </>
      ) : null}
    </Paper>
  );
}

function wordCount(text) {
  if (!text || typeof text !== "string") return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// ID132 — distinct, meaningful word count (ignores case, punctuation and repeated tokens) so junk
// queries like "test test test test" can't be saved just by repeating a word.
const STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "of",
  "to",
  "in",
  "on",
  "for",
  "with",
  "is",
  "are",
  "by",
  "at",
  "as",
  "from",
  "this",
  "that",
  "it",
  "its",
]);
function distinctWordCount(text) {
  const words =
    String(text || "")
      .toLowerCase()
      .match(/[a-z0-9]{2,}/g) || [];
  return new Set(words.filter((w) => !STOP_WORDS.has(w))).size;
}
const MIN_DISTINCT_WORDS = 3;

function normaliseTaxonomyLinks(links) {
  if (!Array.isArray(links)) return [];
  return links.map((l) => ({
    taxonomyId: String(l.taxonomyId || ""),
    categoryId: String(l.categoryId || ""),
    label: l.label ? String(l.label) : undefined,
  }));
}

function normaliseNewsSearchProvidersFromRow(r) {
  const raw = r?.newsSearchProviders;
  const allow = new Set(["gdelt", "bing", "tavily"]);
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_NEWS_PROVIDERS;
  const { list } = raw.reduce(
    (acc, x) => {
      const p = String(x || "")
        .toLowerCase()
        .trim();
      if (!allow.has(p) || acc.seen.has(p)) return acc;
      acc.seen.add(p);
      acc.list.push(p);
      return acc;
    },
    { seen: new Set(), list: [] }
  );
  return list.length ? list : DEFAULT_NEWS_PROVIDERS;
}

function UserSearch({ embedded = false, onChanged }) {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const { role, categoryAttachments: myCategories } = useCurrentUser();
  const canManage = ["RISK_ADMIN", "SUPER_RISK_ADMIN", "CATEGORY_OWNER"].includes(
    (role || "").toUpperCase()
  );

  const listFetched = useRef(false);
  const coachFieldsTouchedRef = useRef(false);
  const coachInFlightRef = useRef(false);
  const skipCoachDebounceOnceRef = useRef(false);
  const formRef = useRef({});

  const [rows, setRows] = useState([]);
  const [taxonomyList, setTaxonomyList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({
    name: "",
    keyword: "",
    source: "ALL",
    theme: "",
    taxonomyLinks: [],
    ingestTier: "daily",
    newsSearchProviders: ["gnews"],
    sourceLinks: [],
  });
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachResult, setCoachResult] = useState(null);
  const [builderMode, setBuilderMode] = useState(true);
  const [builderTopic, setBuilderTopic] = useState("");
  const [builderSector, setBuilderSector] = useState("");
  const [builderAspect, setBuilderAspect] = useState("");

  formRef.current = form;

  const categoryOptions = useMemo(() => {
    if (!taxonomyList?.length) return [];
    return taxonomyList.flatMap((t) => {
      const tName = t.name || "Taxonomy";
      const tId = t.id;
      return (t.Categories || [])
        .filter((cat) => cat?.id)
        .map((cat) => ({
          key: `${tId}:${cat.id}`,
          taxonomyId: tId,
          categoryId: cat.id,
          label: `${tName} › ${cat.name || cat.id}`,
        }));
    });
  }, [taxonomyList]);

  const loadList = useCallback(() => {
    executeRef.current("GET", API_LIST).then((res) => {
      if (res?.success && Array.isArray(res.data)) {
        setRows(res.data);
        if (onChanged) onChanged(res.data);
      }
    });
  }, [onChanged]);

  const loadTaxonomies = useCallback(() => {
    executeRef.current("GET", API_TAXONOMY_ALL).then((res) => {
      if (res?.success && Array.isArray(res.data)) setTaxonomyList(res.data);
    });
  }, []);

  useEffect(() => {
    if (listFetched.current) return;
    listFetched.current = true;
    loadList();
    loadTaxonomies();
  }, [loadList, loadTaxonomies]);

  useEffect(() => {
    if (!builderMode || !dialogOpen) return;
    const assembled = assembleQuery(builderTopic, builderSector, builderAspect);
    setForm((f) => ({ ...f, keyword: assembled }));
    if (assembled.trim()) coachFieldsTouchedRef.current = true;
  }, [builderMode, dialogOpen, builderTopic, builderSector, builderAspect]);

  const tableData = useMemo(
    () =>
      rows.map((r) => ({
        id: r.id,
        name: r.name,
        keyword: r.keyword,
        source: r.source,
        theme: r.theme,
        taxonomyLinks: normaliseTaxonomyLinks(r.taxonomyLinks),
        ingestTier: r.ingestTier === "instant" ? "instant" : "daily",
        ingestTierLabel: INGEST_TIER_LABEL[r.ingestTier === "instant" ? "instant" : "daily"],
        newsSearchProviders: normaliseNewsSearchProvidersFromRow(r),
        providersSummary: normaliseNewsSearchProvidersFromRow(r)
          .map((id) => NEWS_PROVIDER_OPTIONS.find((o) => o.id === id)?.label || id)
          .join(", "),
        taxonomySummary:
          Array.isArray(r.taxonomyLinks) && r.taxonomyLinks.length > 0
            ? r.taxonomyLinks.map((l) => l.label || `${l.taxonomyId?.slice(0, 6)}…`).join("; ")
            : "—",
      })),
    [rows]
  );

  const resetDialogState = () => {
    coachFieldsTouchedRef.current = false;
    skipCoachDebounceOnceRef.current = false;
    setFormError("");
    setCoachResult(null);
    setBuilderTopic("");
    setBuilderSector("");
    setBuilderAspect("");
  };

  const openAdd = () => {
    resetDialogState();
    setEditingId(null);
    // Pre-populate with user's own category attachments (excluding __ALL__ sentinel)
    const myLinks = normaliseTaxonomyLinks(
      (myCategories || []).filter(
        (a) => a && a.categoryId && a.categoryId !== "__ALL__" && a.taxonomyId !== "__ALL__"
      )
    );
    setForm({
      name: "",
      keyword: "",
      source: "ALL",
      theme: "",
      taxonomyLinks: myLinks,
      ingestTier: "daily",
      newsSearchProviders: DEFAULT_NEWS_PROVIDERS,
      sourceLinks: [],
    });
    setBuilderMode(true);
    setDialogOpen(true);
  };

  const openEdit = useCallback(
    (original) => {
      resetDialogState();
      const r = rows.find((x) => x.id === original.id) || original;
      setEditingId(r.id);
      setForm({
        name: r.name || "",
        keyword: r.keyword || "",
        source: r.source || "ALL",
        theme: r.theme || "",
        taxonomyLinks: normaliseTaxonomyLinks(r.taxonomyLinks),
        ingestTier: r.ingestTier === "instant" ? "instant" : "daily",
        newsSearchProviders: normaliseNewsSearchProvidersFromRow(r),
        sourceLinks: Array.isArray(r.sourceLinks) ? r.sourceLinks : [],
      });
      setBuilderMode(false);
      setDialogOpen(true);
    },
    [rows]
  );

  const closeDialog = () => {
    resetDialogState();
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleDelete = useCallback(
    (original) => {
      // ID130 — confirm before deleting; this also removes the search's results + themes.
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        "CAUTION - you are about to delete a user search parameter. This cannot be undone. " +
          "All corresponding search results and Emerging Themes will also be deleted. " +
          "Are you sure you want to proceed with the deletion?"
      );
      if (!confirmed) return;
      executeRef.current("DELETE", API_DELETE(original.id)).then((res) => {
        if (res?.success) loadList();
      });
    },
    [loadList]
  );

  const categoryOptionsRef = useRef([]);
  categoryOptionsRef.current = categoryOptions;

  const myCategoriesRef = useRef([]);
  myCategoriesRef.current = myCategories || [];

  const requestCoaching = useCallback(async () => {
    if (coachInFlightRef.current) return;
    coachInFlightRef.current = true;
    setFormError("");
    setCoachLoading(true);
    setCoachResult(null);
    try {
      const { name, keyword, taxonomyLinks } = formRef.current;
      // Resolve labels for the currently selected categories in the form
      const selectedLabels = taxonomyLinks
        .map((l) => {
          const opt = categoryOptionsRef.current.find(
            (o) => o.taxonomyId === l.taxonomyId && o.categoryId === l.categoryId
          );
          return opt?.label || l.label;
        })
        .filter(Boolean);
      // Also include any user-assigned categories not already in selectedLabels
      const myLabels = myCategoriesRef.current
        .filter((a) => a && a.label && a.categoryId !== "__ALL__" && a.taxonomyId !== "__ALL__")
        .map((a) => a.label)
        .filter((lbl) => lbl && !selectedLabels.includes(lbl));
      const userCategoryContext = [...selectedLabels, ...myLabels];
      const res = await executeRef.current("POST", API_COACH, {
        draftName: name,
        draftKeyword: keyword,
        minWords: MIN_KEYWORD_WORDS,
        taxonomyLabels: categoryOptionsRef.current.map((o) => o.label),
        userCategoryContext,
      });
      if (res?.success && res.data) setCoachResult(res.data);
      else setFormError(res?.message || "Coaching request failed.");
    } catch {
      setFormError("Coaching request failed.");
    } finally {
      coachInFlightRef.current = false;
      setCoachLoading(false);
    }
  }, []);

  const requestCoachingRef = useRef(requestCoaching);
  requestCoachingRef.current = requestCoaching;

  const COACH_DEBOUNCE_MS = 1400;
  useEffect(() => {
    if (!dialogOpen || !canManage) return undefined;
    if (skipCoachDebounceOnceRef.current) {
      skipCoachDebounceOnceRef.current = false;
      return undefined;
    }
    const hasDraft = Boolean(form.name?.trim()) || Boolean(form.keyword?.trim());
    if (!hasDraft || !coachFieldsTouchedRef.current) return undefined;
    const id = window.setTimeout(
      () => requestCoachingRef.current().catch(() => {}),
      COACH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(id);
  }, [dialogOpen, form.keyword, form.name, canManage]);

  const applyCoachingToForm = () => {
    if (!coachResult) return;
    skipCoachDebounceOnceRef.current = true;
    if (typeof coachResult.suggested_query === "string" && coachResult.suggested_query.trim()) {
      setBuilderMode(false);
      setForm((f) => ({ ...f, keyword: coachResult.suggested_query.trim() }));
    }
    const picks = Array.isArray(coachResult.taxonomy_pick) ? coachResult.taxonomy_pick : [];
    if (picks.length > 0) {
      const pickSet = new Set(picks);
      const links = categoryOptions
        .filter((o) => pickSet.has(o.label))
        .map((o) => ({ taxonomyId: o.taxonomyId, categoryId: o.categoryId, label: o.label }));
      if (links.length > 0) setForm((f) => ({ ...f, taxonomyLinks: links }));
    }
  };

  const save = async () => {
    setFormError("");
    const wc = wordCount(form.keyword);
    if (wc < MIN_KEYWORD_WORDS) {
      setFormError(
        `Need at least ${MIN_KEYWORD_WORDS} words (currently ${wc}). Use Topic + Sector + Risk angle, or apply the suggested query.`
      );
      return;
    }
    // ID132 — block low-quality / repetitive queries (e.g. "test test test test").
    const dwc = distinctWordCount(form.keyword);
    if (dwc < MIN_DISTINCT_WORDS) {
      setFormError(
        `This query looks too generic or repetitive (only ${dwc} distinct word${
          dwc === 1 ? "" : "s"
        }). Enter a specific query — Topic + Sector + Risk angle — or apply the suggested query.`
      );
      return;
    }
    if (!form.name?.trim()) {
      setFormError("Name is required.");
      return;
    }
    const links = normaliseTaxonomyLinks(form.taxonomyLinks).map((l) => {
      const opt = categoryOptions.find(
        (o) => o.taxonomyId === l.taxonomyId && o.categoryId === l.categoryId
      );
      return { taxonomyId: l.taxonomyId, categoryId: l.categoryId, label: opt?.label || l.label };
    });
    const body = {
      name: form.name.trim(),
      keyword: form.keyword.trim(),
      source: form.source,
      theme: form.theme?.trim() || "",
      taxonomyLinks: links,
      // ID134 — cadence is no longer user-configurable; always daily.
      ingestTier: "daily",
      newsSearchProviders: DEFAULT_NEWS_PROVIDERS,
      sourceLinks: (form.sourceLinks || []).map((s) => String(s || "").trim()).filter(Boolean),
    };
    const url = editingId ? API_UPDATE(editingId) : API_CREATE;
    const method = editingId ? "PUT" : "POST";
    const res = await execute(method, url, body);
    if (res?.success) {
      closeDialog();
      loadList();
    } else if (res?.message) {
      setFormError(res.message);
    }
  };

  const kwWords = wordCount(form.keyword);
  const canRequestCoach = canManage && Boolean(form.keyword?.trim());

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Saved search",
        size: 160,
        muiTableBodyCellProps: { sx: { verticalAlign: "top", wordBreak: "break-word" } },
      },
      {
        accessorKey: "keyword",
        header: "Keywords / query",
        size: 280,
        muiTableBodyCellProps: { sx: { verticalAlign: "top", wordBreak: "break-word" } },
      },
      {
        accessorKey: "taxonomySummary",
        header: "Taxonomy link",
        size: 200,
        muiTableBodyCellProps: { sx: { verticalAlign: "top", wordBreak: "break-word" } },
      },
      // ID135 — "News providers" and "Source" columns removed; source is now set in the pop-up.
      // ID134 — "Scan" (cadence) column removed; cadence is no longer user-configurable (always daily).
      ...(canManage
        ? [
            {
              id: "actions",
              header: "Actions",
              size: 88,
              enableSorting: false,
              muiTableBodyCellProps: {
                sx: { verticalAlign: "top", textAlign: "right", whiteSpace: "nowrap" },
              },
              Cell: ({ row }) => (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(row.original)}
                      aria-label="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.original)}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          ]
        : []),
    ],
    [canManage, openEdit, handleDelete]
  );

  const inner = (
    <>
      <Grid container spacing={2} sx={{ p: embedded ? 0 : 2 }}>
        {!embedded && (
          <Grid item xs={12}>
            <Typography variant="h5" component="h1">
              User search
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Saved searches
            </Typography>
            {canManage ? (
              <MDButton
                onClick={openAdd}
                backgroundColor="#52009b"
                variant="contained"
                color="#52009b"
                sx={{ color: "#f7f4fb" }}
              >
                Add search
              </MDButton>
            ) : null}
          </Box>
          <INDataTable
            columns={columns}
            data={tableData}
            cardSx={{ mb: 0, boxShadow: "none" }}
            enablePagination={tableData.length > 10}
            enableStickyHeader
            localization={{ noRecordsToDisplay: "No saved searches yet." }}
            muiTableContainerProps={{ sx: { maxHeight: { xs: "none", md: "min(70vh, 640px)" } } }}
            muiTableProps={{ sx: { tableLayout: "fixed" } }}
          />
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingId ? "Edit saved search" : "New saved search"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1, minHeight: 420 }}>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Name */}
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => {
                coachFieldsTouchedRef.current = true;
                setForm((f) => ({ ...f, name: e.target.value }));
              }}
              fullWidth
              required
              size="small"
            />

            {/* ── Query builder ───────────────────────────────────── */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  Search query
                </Typography>
                <MDButton
                  size="small"
                  variant="text"
                  onClick={() => (builderMode ? setBuilderMode(false) : setBuilderMode(true))}
                  sx={{ color: "#52009b", p: 0, minWidth: 0, fontSize: 12 }}
                >
                  {builderMode ? "Expert mode" : "Guided builder"}
                </MDButton>
              </Box>

              {builderMode ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Starter templates */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {STARTER_TEMPLATES.map((tmpl) => (
                      <Chip
                        key={tmpl.label}
                        label={tmpl.label}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          coachFieldsTouchedRef.current = true;
                          setBuilderTopic(tmpl.topic);
                          setBuilderSector(tmpl.sector);
                          setBuilderAspect(tmpl.aspect);
                        }}
                        sx={{
                          cursor: "pointer",
                          borderColor: "#52009b",
                          color: "#52009b",
                          fontSize: "0.72rem",
                        }}
                      />
                    ))}
                  </Box>

                  <TextField
                    label="Topic"
                    placeholder="e.g. jet fuel prices"
                    value={builderTopic}
                    onChange={(e) => {
                      coachFieldsTouchedRef.current = true;
                      setBuilderTopic(e.target.value);
                    }}
                    fullWidth
                    required
                    size="small"
                    helperText="Multi-word topics are auto-quoted for exact matching."
                  />
                  <TextField
                    label="Sector / context"
                    placeholder="e.g. aviation airline airport"
                    value={builderSector}
                    onChange={(e) => {
                      coachFieldsTouchedRef.current = true;
                      setBuilderSector(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    helperText="Keeps results within your industry."
                  />
                  <TextField
                    label="Risk angle"
                    placeholder="e.g. disruption shortage compliance incident cost"
                    value={builderAspect}
                    onChange={(e) => {
                      coachFieldsTouchedRef.current = true;
                      setBuilderAspect(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    helperText="The risk dimension — shortage, breach, regulation, safety."
                  />

                  {/* Assembled preview — only shown when there's something */}
                  {form.keyword ? (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.75,
                        bgcolor: "rgba(82,0,155,0.04)",
                        border: "1px solid rgba(82,0,155,0.12)",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Assembled query:{" "}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", display: "inline", color: "#1a237e" }}
                      >
                        {form.keyword}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <TextField
                  label="Keywords / query"
                  value={form.keyword}
                  onChange={(e) => {
                    coachFieldsTouchedRef.current = true;
                    setForm((f) => ({ ...f, keyword: e.target.value }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey)) return;
                    e.preventDefault();
                    if (canRequestCoach && !coachLoading) requestCoaching().catch(() => {});
                  }}
                  placeholder={`"jet fuel prices" aviation airline supply shortage`}
                  helperText={`Use quoted phrases for exact match. Ctrl+Enter to coach now. Min ${MIN_KEYWORD_WORDS} words.`}
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  size="small"
                  error={kwWords > 0 && kwWords < MIN_KEYWORD_WORDS}
                />
              )}

              {/* Quality bar — one line, always visible when query exists */}
              {form.keyword ? (
                <Box sx={{ mt: 0.75 }}>
                  <QualityMeter query={form.keyword} />
                </Box>
              ) : null}
            </Box>

            {/* ── Coaching ────────────────────────────────────────── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MDButton
                variant="outlined"
                onClick={requestCoaching}
                disabled={!canRequestCoach || coachLoading}
                sx={{ borderColor: "#52009b", color: "#52009b" }}
                size="small"
              >
                {coachLoading ? "Coaching…" : "Improve query"}
              </MDButton>
              {coachLoading ? <CircularProgress size={18} /> : null}
            </Box>

            <CoachResultPanel coachResult={coachResult} onApply={applyCoachingToForm} />

            {/* ID134 — "Scan cadence" field removed; cadence is no longer user-configurable
                (all user searches default to daily). */}

            {/* ID135 — optional specific source URLs to monitor (none / one / many). */}
            <TextField
              label="Specific sources (optional)"
              placeholder="https://…  (one source URL per line)"
              value={(form.sourceLinks || []).join("\n")}
              onChange={(e) =>
                setForm((f) => ({ ...f, sourceLinks: e.target.value.split(/\r?\n/) }))
              }
              size="small"
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              error={(form.sourceLinks || []).some(
                (s) => s.trim() && !/^https?:\/\//i.test(s.trim())
              )}
              helperText="Leave blank to scan all sources, or add one or more specific source URLs to monitor."
            />

            <Autocomplete
              multiple
              options={categoryOptions}
              value={form.taxonomyLinks
                .map((l) =>
                  categoryOptions.find(
                    (o) => o.taxonomyId === l.taxonomyId && o.categoryId === l.categoryId
                  )
                )
                .filter(Boolean)}
              onChange={(_, v) =>
                setForm((f) => ({
                  ...f,
                  taxonomyLinks: (v || []).map((o) => ({
                    taxonomyId: o.taxonomyId,
                    categoryId: o.categoryId,
                    label: o.label,
                  })),
                }))
              }
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a?.key === b?.key}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} label="Taxonomy categories (optional)" size="small" />
              )}
            />
            {!editingId &&
              (myCategories || []).filter(
                (a) => a && a.label && a.categoryId !== "__ALL__" && a.taxonomyId !== "__ALL__"
              ).length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                  Pre-filled from your profile. Adjust as needed — these also guide the AI query
                  coach.
                </Typography>
              )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <MDButton onClick={closeDialog}>Cancel</MDButton>
          <MDButton
            backgroundColor="#52009b"
            color="#52009b"
            sx={{ color: "#f7f4fb" }}
            variant="contained"
            onClick={save}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );

  return embedded ? (
    inner
  ) : (
    <DashboardLayout>
      <DashboardNavbar />
      {inner}
    </DashboardLayout>
  );
}

export default UserSearch;
